import { useTranslation } from "react-i18next";

/** `Intl`-based formatters bound to the active UI language. */
export function useFormat() {
  const { i18n } = useTranslation();
  const language = i18n.language;
  return {
    date: (value: string | Date) =>
      new Intl.DateTimeFormat(language, { dateStyle: "medium" }).format(new Date(value)),
    /** A calendar date (`YYYY-MM-DD`), shown as written regardless of the viewer's time zone. */
    calendarDate: (value: string) =>
      new Intl.DateTimeFormat(language, { dateStyle: "medium", timeZone: "UTC" }).format(
        new Date(`${value}T00:00:00Z`),
      ),
    dateTime: (value: string | Date) =>
      new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
        new Date(value),
      ),
    number: (value: number) => new Intl.NumberFormat(language).format(value),
    /** A byte count in the largest fitting binary unit ("1,5 MB"). */
    bytes: (value: number) => {
      const units = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;
      let size = value;
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index++;
      }
      return new Intl.NumberFormat(language, {
        style: "unit",
        unit: units[index],
        unitDisplay: "narrow",
        maximumFractionDigits: index === 0 ? 0 : 1,
      }).format(size);
    },
    /** A duration given in milliseconds, e.g. "1 h 2 min 3 s". */
    duration: (ms: number) => {
      const total = Math.max(0, Math.round(ms / 1000));
      const parts: [number, "hour" | "minute" | "second"][] = [
        [Math.floor(total / 3600), "hour"],
        [Math.floor((total % 3600) / 60), "minute"],
        [total % 60, "second"],
      ];
      return parts
        .filter(
          ([amount, unit], _index, all) =>
            amount > 0 || (unit === "second" && all.every(([a]) => a === 0)),
        )
        .map(([amount, unit]) =>
          new Intl.NumberFormat(language, { style: "unit", unit, unitDisplay: "narrow" }).format(
            amount,
          ),
        )
        .join(" ");
    },
    /** An average rating with at most one decimal ("4,3" in German, "4.3" in English). */
    rating: (value: number) =>
      new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value),
  };
}
